import type { ErrorCode, ErrorDiagnostics } from '@movingimage-evp/player-library';

import { useCallback, useEffect, useRef, useState } from 'react';

import { ViewerSourceMode } from '../../../../../generated/graphql-viewer';
import { devLog } from '../../../../../utils';
import { sendErrorToNewRelic } from '../../utility-functions';

interface UseStreamSelectionParams {
  viewerSourceMode?: ViewerSourceMode;
  primaryStreamReady?: boolean;
  backupStreamReady?: boolean;
  retryCount: number;
  resetRetryCount: () => void;
}

interface StreamSelectionResult {
  streamToUse: StreamType;
  handleStreamError: (
    playerErrorCode: ErrorCode,
    errorDiagnostics?: ErrorDiagnostics,
    visitorId?: string
  ) => RetryAction | undefined;
  switchStream: () => void;
  resetStreamSelection: () => void;
  isRetryStopped: () => boolean;
}
type RetryAction = 'retry' | 'switch' | 'stop';
type StreamType = 'primary' | 'backup' | null;

const MAX_RETRIES = 3;
const MAX_SWITCHES = 3;
const SWITCH_COUNT_RESET_MS = 60_000;

function getAlternative(stream: 'primary' | 'backup'): 'primary' | 'backup' {
  return stream === 'primary' ? 'backup' : 'primary';
}

export function useStreamSelection({
  viewerSourceMode,
  primaryStreamReady,
  backupStreamReady,
  retryCount,
  resetRetryCount,
}: UseStreamSelectionParams): StreamSelectionResult {
  const getInitialStream = useCallback((): StreamType => {
    if (viewerSourceMode === ViewerSourceMode.FORCE_PRIMARY) return 'primary';
    if (viewerSourceMode === ViewerSourceMode.FORCE_BACKUP) return 'backup';
    if (viewerSourceMode === ViewerSourceMode.AUTO) {
      if (primaryStreamReady) return 'primary';
      if (backupStreamReady) return 'backup';
    }
    return null;
  }, [backupStreamReady, primaryStreamReady, viewerSourceMode]);

  const [streamToUse, setStreamToUse] = useState<StreamType>(getInitialStream);

  const retryingStoppedRef = useRef(false);
  const switchCountRef = useRef(0);
  const switchCountResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const forcePrimary = viewerSourceMode === ViewerSourceMode.FORCE_PRIMARY;
  const forceBackup = viewerSourceMode === ViewerSourceMode.FORCE_BACKUP;
  const autoViewerSource = viewerSourceMode === ViewerSourceMode.AUTO;

  const previousStateRef = useRef({
    forcePrimary,
    forceBackup,
    primaryReady: primaryStreamReady || false,
    backupReady: backupStreamReady || false,
  });

  useEffect(() => {
    const prev = previousStateRef.current;
    const inputsChanged =
      prev.forcePrimary !== forcePrimary ||
      prev.forceBackup !== forceBackup ||
      prev.primaryReady !== (primaryStreamReady || false) ||
      prev.backupReady !== (backupStreamReady || false);

    if (!inputsChanged) {
      return;
    }

    const prevAltReady = streamToUse === 'primary' ? prev.backupReady : prev.primaryReady;

    let nextStream: StreamType | undefined;

    if (forcePrimary && !prev.forcePrimary) nextStream = 'primary';
    if (forceBackup && !prev.forceBackup) nextStream = 'backup';

    if (nextStream === undefined && autoViewerSource) {
      if (streamToUse) {
        const alt = getAlternative(streamToUse);
        const currentReady = streamToUse === 'primary' ? primaryStreamReady : backupStreamReady;
        const altReady = streamToUse === 'primary' ? backupStreamReady : primaryStreamReady;
        const prevCurrentReady = streamToUse === 'primary' ? prev.primaryReady : prev.backupReady;

        if (!currentReady && prevCurrentReady) {
          nextStream = altReady ? alt : null;
        }

        /**
         * Handle the case, that retrying is stopped and
         * after this the alternative server becomes ready.
         * In this case we want to switch to the ready stream.
         */
        if (retryingStoppedRef.current && altReady && !prevAltReady) {
          retryingStoppedRef.current = false;
          nextStream = alt;
        }
      } else {
        if (primaryStreamReady) nextStream = 'primary';
        else if (backupStreamReady) nextStream = 'backup';
      }
    }

    previousStateRef.current = {
      forcePrimary,
      forceBackup,
      primaryReady: primaryStreamReady || false,
      backupReady: backupStreamReady || false,
    };

    if (nextStream !== undefined && nextStream !== streamToUse) {
      retryingStoppedRef.current = false;
      switchCountRef.current = 0;
      resetRetryCount();
      setStreamToUse(nextStream);
    }
  }, [
    autoViewerSource,
    backupStreamReady,
    forceBackup,
    forcePrimary,
    primaryStreamReady,
    resetRetryCount,
    streamToUse,
  ]);

  const identifyAction = useCallback((): { action: RetryAction; reason?: string } => {
    // Forced mode: only retry on the forced stream
    if (forcePrimary || forceBackup) {
      const forced = forcePrimary ? 'primary' : 'backup';
      const forcedReady = forcePrimary ? primaryStreamReady : backupStreamReady;

      if (forcedReady && retryCount < MAX_RETRIES) return { action: 'retry' };

      const reason =
        retryCount >= MAX_RETRIES
          ? 'Max retries reached'
          : `${forced === 'primary' ? 'Primary' : 'Backup'} stream not ready`;
      return { action: 'stop', reason };
    }

    // Auto mode: try current stream, then switch to alternative
    if (autoViewerSource && streamToUse) {
      const currentReady = streamToUse === 'primary' ? primaryStreamReady : backupStreamReady;
      const altReady = streamToUse === 'primary' ? backupStreamReady : primaryStreamReady;
      const streamLabel = streamToUse === 'primary' ? 'Primary' : 'Backup';

      if (altReady) {
        if (currentReady && retryCount < 1) return { action: 'retry' };
        if (switchCountRef.current < MAX_SWITCHES) return { action: 'switch' };

        return { action: 'stop', reason: 'Max switches reached' };
      }

      if (currentReady && retryCount < MAX_RETRIES) return { action: 'retry' };

      return {
        action: 'stop',
        reason: retryCount >= MAX_RETRIES ? 'Max retries reached' : `${streamLabel} stream not ready`,
      };
    }

    return { action: 'stop', reason: 'No valid stream configuration' };
  }, [autoViewerSource, backupStreamReady, streamToUse, forceBackup, forcePrimary, primaryStreamReady, retryCount]);

  const switchStream = useCallback(() => {
    switchCountRef.current += 1;
    devLog('🔄 Switching stream. Switch count: ', switchCountRef.current);
    if (switchCountResetTimerRef.current) clearTimeout(switchCountResetTimerRef.current);
    switchCountResetTimerRef.current = setTimeout(() => {
      switchCountRef.current = 0;
    }, SWITCH_COUNT_RESET_MS);
    setStreamToUse((current) => (current === 'primary' ? 'backup' : 'primary'));
  }, []);

  const resetStreamSelection = useCallback(() => {
    retryingStoppedRef.current = false;
    switchCountRef.current = 0;
    if (switchCountResetTimerRef.current) {
      clearTimeout(switchCountResetTimerRef.current);
      switchCountResetTimerRef.current = null;
    }
    setStreamToUse(getInitialStream());
    resetRetryCount();
  }, [getInitialStream, resetRetryCount]);

  const handleStreamError = useCallback(
    (playerErrorCode: ErrorCode, errorDiagnostics?: ErrorDiagnostics, visitorId?: string) => {
      const result = identifyAction();
      const nextStream = streamToUse ? getAlternative(streamToUse) : 'unknown';
      const message =
        result.action === 'retry'
          ? `Retrying ${streamToUse} stream (${retryCount + 1}. attempt)`
          : result.action === 'switch'
            ? `Switching to ${nextStream} stream`
            : `Stopping retries (${result.reason})`;

      sendErrorToNewRelic({
        message: `Player Error ${playerErrorCode} (Action: ${message})`,
        customFields: {
          playerErrorCode,
          action: result.action,
          retryCount: result.action === 'retry' ? retryCount + 1 : undefined,
          visitorId,
          diagnosticsBandwidthKbps: errorDiagnostics?.bandwidth.measuredBitrateKbps,
          diagnosticsRenditionKbps: errorDiagnostics?.bandwidth.currentRenditionBitrateKbps,
          diagnosticsBufferAheadS: errorDiagnostics?.buffering?.bufferAheadS,
          diagnosticsBufferStatus: errorDiagnostics?.buffering?.bufferStatus,
          diagnosticsSegTotal: errorDiagnostics?.segmentStats?.totalRequests,
          diagnosticsSegErrored: errorDiagnostics?.segmentStats?.errored,
          diagnosticsSegTimedout: errorDiagnostics?.segmentStats?.timedout,
          diagnosticsSegAborted: errorDiagnostics?.segmentStats?.aborted,
          diagnosticsPlaylistLoaded: errorDiagnostics?.failedResource?.playlistLoaded,
          diagnosticsSegmentsFailed: errorDiagnostics?.failedResource?.segmentsFailed,
          diagnosticsLikelyCause: errorDiagnostics?.failedResource?.likelyCause,
        },
      });

      devLog(`⚠️ Stream error action: ${result.action}`, result.reason ?? '');

      if (result.action === 'switch') {
        switchStream();
        resetRetryCount();
      } else if (result.action === 'stop') {
        retryingStoppedRef.current = true;
      }

      return result.action;
    },
    [identifyAction, streamToUse, retryCount, switchStream, resetRetryCount]
  );

  useEffect(() => {
    return () => {
      if (switchCountResetTimerRef.current) clearTimeout(switchCountResetTimerRef.current);
    };
  }, []);

  return {
    streamToUse,
    handleStreamError,
    switchStream,
    resetStreamSelection,
    isRetryStopped: () => retryingStoppedRef.current,
  };
}
